import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, streamMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  DEFAULT_SHARE_DURATION_HOURS,
  REVIEW_SECTION_LABEL,
  REVIEW_TYPE_LABEL,
  SHARE_DURATION_OPTIONS,
  type Attachment,
  type ChecklistItem,
  type Deal,
  type DocumentVersion,
  type Message,
  type Review,
  type ShareLink,
  type TeamConvention,
} from "@docreview/shared";
import Markdown from "../components/Markdown";
import StreamProgress from "../components/StreamProgress";
import { ChecklistDrawer } from "../components/ChecklistDrawer";
import { DocumentsPanel } from "./Deal";

const LONG_REVIEW_COST_USD = 2;
const LONG_REVIEW_MESSAGE_COUNT = 15;
const SCROLL_BOTTOM_THRESHOLD_PX = 80;

// Hide the trailing structured-items block from the live stream view; the
// server strips it from the persisted message after the stream completes.
// The model emits either `checklist-json` or plain `json` as the fence tag.
// We anchor on the fence + the start of an `"items"` array so mid-message
// quoted JSON snippets don't trigger a false strip.
function stripChecklistFence(text: string): string {
  const m = text.match(/```(?:checklist-json|json)\s*\n\s*\{[\s\S]*?"items"/i);
  if (!m || m.index === undefined) return text;
  return text.slice(0, m.index).trimEnd();
}

const STAGE_LABEL: Record<string, string> = {
  first_draft: "First draft",
  redline_response: "Redline response",
  final_check: "Final check",
};

export default function ReviewPage() {
  const { dealId, reviewId } = useParams<{ dealId: string; reviewId: string }>();
  const { user } = useAuth();
  const isAdmin = useAuth().isAdmin === true;

  const [deal, setDeal] = useState<Deal | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [input, setInput] = useState("");
  const [err, setErr] = useState<string | null>(null);
  // Set true between "stream finished" and "checklist items refetched" so
  // the user sees a clear loading state instead of staring at an empty
  // moment when the model wraps up.
  const [generatingChecklist, setGeneratingChecklist] = useState(false);
  // Progress while the model is silent. Opus 5.5 thinks for minutes on a
  // full review before the first word streams; without this the bubble
  // is blank and a slow turn looks identical to a dead one.
  const [streamStatus, setStreamStatus] = useState<{ phase: string; detail?: string } | null>(
    null,
  );
  const [thinkingText, setThinkingText] = useState("");
  const [streamStartedAt, setStreamStartedAt] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Tracks whether we've already pre-filled the composer for a fresh review,
  // so we don't overwrite the user's edits on subsequent refreshes.
  const prefilledRef = useRef(false);
  // Auto-opens the checklist drawer the first time items appear for this
  // review. Tracking via a ref (rather than effect on checklistItems.length)
  // so it only fires on the transition from "no items" → "items", not on
  // every refresh. Resets when navigating between reviews.
  const autoOpenedRef = useRef(false);
  // Aborts the in-flight streaming turn when the user hits Stop.
  const abortRef = useRef<AbortController | null>(null);

  async function refresh() {
    const [d, r, a, v, m, c] = await Promise.all([
      api.getDeal(dealId!),
      api.getReview(dealId!, reviewId!),
      api.listAttachments(dealId!),
      api.listVersions(dealId!),
      api.listMessages(dealId!, reviewId!),
      api.listChecklist(dealId!, reviewId!),
    ]);
    setDeal(d.deal);
    setReview(r.review);
    setAttachments(a.attachments);
    setVersions(v.versions);
    setMessages(m.messages);
    setChecklistItems(c.items);
  }

  useEffect(() => {
    // New review: reset per-review one-shot flags so this review gets
    // its own auto-open and its own pre-fill.
    autoOpenedRef.current = false;
    prefilledRef.current = false;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, reviewId]);

  // Auto-open the drawer the first time checklist items exist for this
  // review (covers both initial load on a previously-reviewed page AND
  // the moment items first appear after a full-review turn). Only fires
  // once per review per session, and not when the review is already
  // submitted (read-only — no action needed from the user).
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!review || review.submittedAt) return;
    if (checklistItems.length === 0) return;
    autoOpenedRef.current = true;
    setDrawerOpen(true);
  }, [checklistItems.length, review]);

  // Pre-fill the composer with the standard kickoff prompt on a fresh review
  // (no messages yet). Uses the deal's ticker if set, otherwise the title.
  useEffect(() => {
    if (prefilledRef.current) return;
    if (!deal) return;
    if (messages.length > 0) return;
    if (input.length > 0) return;
    if (!review) return;
    const label = (deal.ticker?.trim() || deal.title).trim();
    setInput(kickoffText(review, label));
    prefilledRef.current = true;
  }, [deal, review, messages.length, input.length]);

  useEffect(() => {
    if (!isAtBottom) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight });
  }, [messages, streamText, isAtBottom]);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distanceFromBottom < SCROLL_BOTTOM_THRESHOLD_PX);
  }

  function jumpToLatest() {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setIsAtBottom(true);
  }

  // Refetch everything a turn may have changed (review stage, checklist
  // items, deal cost) and the message list (replaces it wholesale, so a
  // server-persisted partial can't duplicate). Shared by the normal and
  // stopped paths.
  async function refetchAfterTurn() {
    const [{ review: r }, { deal: d }, { items: c }, { messages: ms }] =
      await Promise.all([
        api.getReview(dealId!, reviewId!),
        api.getDeal(dealId!),
        api.listChecklist(dealId!, reviewId!),
        api.listMessages(dealId!, reviewId!),
      ]);
    setReview(r);
    setDeal(d);
    setChecklistItems(c);
    setMessages(ms);
  }

  async function send(contentOverride?: string) {
    const content = (contentOverride ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setStreaming(true);
    setStreamText("");
    setErr(null);
    setIsAtBottom(true);
    setGeneratingChecklist(false);
    setStreamStatus({ phase: "preparing", detail: "Sending your message…" });
    setThinkingText("");
    setStreamStartedAt(Date.now());
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await streamMessage(
        dealId!,
        reviewId!,
        content,
        {
          onUserMessage: (m) => setMessages((prev) => [...prev, m]),
          onDelta: (t) => setStreamText((prev) => prev + t),
          onAssistantMessage: (m) => {
            setMessages((prev) => [...prev, m]);
            setStreamText("");
            setThinkingText("");
          },
          onChecklistGenerating: () => setGeneratingChecklist(true),
          onStatus: (phase, detail) => {
            setStreamStatus({ phase, detail });
            // A continuation starts a fresh think; drop the old summary.
            if (phase === "continuing" || phase === "sending") setThinkingText("");
          },
          onThinking: (t) => setThinkingText((prev) => prev + t),
          onError: (msg) => setErr(msg),
        },
        ac.signal,
      );
      // Keep the "generating" banner up until the refetch finishes so the
      // user sees one continuous loading state.
      await refetchAfterTurn();
    } catch (e) {
      // Stop button / navigation aborts the fetch — that's a deliberate
      // halt, not an error. The backend persists the partial response;
      // wait a beat for that write, then reconcile from the server.
      if (e instanceof DOMException && e.name === "AbortError") {
        setStreamText("");
        await new Promise((r) => setTimeout(r, 800));
        await refetchAfterTurn().catch(() => {});
      } else {
        setErr(e instanceof Error ? e.message : String(e));
      }
    } finally {
      abortRef.current = null;
      setStreaming(false);
      setGeneratingChecklist(false);
      setStreamStatus(null);
      setThinkingText("");
      setStreamStartedAt(null);
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  const totalUsage = useMemo(() => {
    return messages.reduce(
      (acc, m) => {
        if (m.usage) {
          acc.in += m.usage.inputTokens;
          acc.out += m.usage.outputTokens;
          acc.cwrite += m.usage.cacheCreateTokens;
          acc.cread += m.usage.cacheReadTokens;
          acc.cost += m.usage.costUsd;
        }
        return acc;
      },
      { in: 0, out: 0, cwrite: 0, cread: 0, cost: 0 },
    );
  }, [messages]);

  if (!deal || !review) return <div className="p-8 text-slate-500">Loading…</div>;

  const reviewIsLong =
    review.totalCostUsd >= LONG_REVIEW_COST_USD ||
    messages.length >= LONG_REVIEW_MESSAGE_COUNT;

  return (
    <div className="max-w-6xl mx-auto px-4 py-4">
      <div className="mb-4">
        <Link
          to={`/deals/${dealId}`}
          className="text-sm text-slate-500 hover:text-ink inline-flex items-center gap-1"
        >
          <span>←</span> {deal.title}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <aside className="space-y-4">
          <div className="card p-4">
            <h2 className="font-semibold">{review.title}</h2>
            <div className="text-xs text-slate-500 mt-1">
              {/* Prefer the new reviewType label when present; fall back to
                  the legacy stage label for reviews created before the
                  reviewType field existed. Append the linked version
                  (e.g. "Document Review · v2") when set. */}
              {review.reviewType
                ? REVIEW_TYPE_LABEL[review.reviewType]
                : (STAGE_LABEL[review.reviewStage] ?? review.reviewStage)}
              {(() => {
                if (!review.versionId) return null;
                const v = versions.find((x) => x.id === review.versionId);
                if (!v) return null;
                const prior =
                  review.priorVersionId &&
                  versions.find((x) => x.id === review.priorVersionId);
                return (
                  <>
                    {" · v"}
                    {v.order}
                    {prior ? ` vs v${prior.order}` : ""}
                  </>
                );
              })()}
            </div>
            {(review.quickPass || (review.skipSections?.length ?? 0) > 0) && (
              <div className="text-xs mt-1.5 rounded bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1">
                {review.quickPass
                  ? "Quick pass — not a full review"
                  : `Skipped: ${review.skipSections!.map((s) => REVIEW_SECTION_LABEL[s]).join(", ")}`}
              </div>
            )}
            <div className="text-xs text-slate-500 mt-1">
              {deal.title} · {deal.dealType}
            </div>
            {deal.notes && deal.notes.trim().length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Deal notes
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">
                  {deal.notes}
                </div>
              </div>
            )}
            {isAdmin && (
              <div className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
                <div>Model: {review.model}</div>
                <div>Total: ${review.totalCostUsd.toFixed(4)}</div>
                <div className="text-slate-400 mt-1">
                  in {totalUsage.in.toLocaleString()} / out{" "}
                  {totalUsage.out.toLocaleString()} tokens
                  <br />
                  cache w {totalUsage.cwrite.toLocaleString()} / r{" "}
                  {totalUsage.cread.toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {reviewIsLong && (
            <div className="card p-3 bg-amber-50 border-amber-200">
              <div className="text-xs font-semibold text-amber-900 mb-1">
                Review getting long
              </div>
              <div className="text-xs text-amber-900/90">
                {messages.length} messages
                {isAdmin ? `, $${review.totalCostUsd.toFixed(2)} so far` : ""}. Consider
                starting a new review on the deal page for unrelated questions.
              </div>
              <Link
                to={`/deals/${dealId}`}
                className="mt-2 text-xs font-medium text-amber-900 hover:underline inline-block"
              >
                Back to deal →
              </Link>
            </div>
          )}

          <ChecklistCard
            review={review}
            items={checklistItems}
            onOpen={() => setDrawerOpen(true)}
            rerunDisabled={streaming}
            onRerun={async () => {
              // Reset the done-but-empty review, then kick the full review
              // off immediately so it's one click for the attorney.
              try {
                await api.rerunReview(dealId!, reviewId!);
              } catch (e) {
                setErr(e instanceof Error ? e.message : String(e));
                return;
              }
              const { review: r } = await api.getReview(dealId!, reviewId!);
              setReview(r);
              void send(kickoffText(r));
            }}
          />

          <DocumentsPanel
            dealId={dealId!}
            attachments={attachments}
            versions={versions}
            onChange={refresh}
            variant="edit"
          />

          {/* Private deals can't be shared externally — the backend
              refuses to mint a link, so don't offer one. */}
          {deal.visibility !== "private" && (
            <SharesPanel dealId={dealId!} reviewId={reviewId!} />
          )}

          <ConventionsPanel isAdmin={isAdmin} />
        </aside>

        <section className="flex flex-col h-[calc(100vh-9rem)] relative min-w-0">
          {(() => {
            // Persistent CTA: items exist, drawer closed, unreviewed items
            // left, review not submitted. Sits above the scrollable message
            // area so it stays visible regardless of scroll position.
            if (drawerOpen) return null;
            if (!review || review.submittedAt) return null;
            if (checklistItems.length === 0) return null;
            const reviewedCount = checklistItems.filter((i) => !!i.status).length;
            if (reviewedCount >= checklistItems.length) return null;
            const remaining = checklistItems.length - reviewedCount;
            return (
              <button
                className="mb-3 w-full rounded-md border border-indigo-300 bg-indigo-50 px-4 py-2.5 text-sm font-medium text-indigo-900 hover:bg-indigo-100 transition flex items-center justify-between"
                onClick={() => setDrawerOpen(true)}
              >
                <span>
                  {remaining} document checklist item{remaining === 1 ? "" : "s"} waiting for
                  your review
                </span>
                <span className="text-indigo-700">Open checklist →</span>
              </button>
            );
          })()}
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex-1 min-w-0 overflow-y-auto space-y-4 pr-1"
          >
            {messages.length === 0 && !streaming && (
              <div className="text-slate-500 text-sm card p-4">
                We've drafted a kickoff prompt for you. Edit it or send as-is — the assistant
                will ask any clarifying questions it needs, otherwise it'll run the full
                review directly.
              </div>
            )}
            {messages.map((m) => (
              <MessageView key={m.id} m={m} isAdmin={isAdmin} />
            ))}
            {streaming && (
              <div className="card p-4 bg-slate-50">
                <div className="text-xs text-slate-500 mb-1">Assistant (streaming…)</div>
                {streamText && <Markdown>{stripChecklistFence(streamText)}</Markdown>}
                {/* Progress stays up whenever the model is silent — before
                    the first word, and again during an auto-continuation,
                    when the partial prose is already on screen but the
                    model is thinking through the rest. */}
                {streamStatus?.phase !== "writing" && (
                  <div className={streamText ? "mt-3 pt-3 border-t border-slate-200" : ""}>
                    <StreamProgress
                      label={streamStatus?.detail}
                      thinking={thinkingText}
                      startedAt={streamStartedAt}
                      longRunningHint={
                        checklistItems.length === 0 && review.stage !== "done"
                          ? "A full review of a large document set usually takes several minutes. It's still running — keep this tab open."
                          : undefined
                      }
                    />
                  </div>
                )}
              </div>
            )}
            {generatingChecklist && (
              <div className="card p-3 bg-blue-50 border-blue-200 text-sm text-blue-900 flex items-center gap-2">
                <Spinner />
                <span>Generating document checklist… this can take a few seconds.</span>
              </div>
            )}
            {err && <div className="card p-4 bg-red-50 text-red-700 text-sm">{err}</div>}
          </div>

          {!isAtBottom && (
            <button
              className="absolute right-4 bottom-28 rounded-full bg-ink text-white text-xs px-3 py-1.5 shadow-lg hover:bg-slate-700 transition flex items-center gap-1"
              onClick={jumpToLatest}
            >
              <span>↓</span> Jump to latest
            </button>
          )}

          <div className="mt-3 flex gap-2">
            <textarea
              className="input flex-1 resize-none"
              rows={3}
              placeholder="Send a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
              }}
            />
            {streaming ? (
              <button
                className="btn-secondary self-end"
                onClick={stop}
                title="Stop generating — keeps what's been written so far"
              >
                ◼ Stop
              </button>
            ) : (
              <button
                className="btn-primary self-end"
                disabled={!input.trim()}
                onClick={() => send()}
              >
                Send
              </button>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-1">⌘/Ctrl+Enter to send</div>
        </section>
      </div>

      <ChecklistDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        dealId={dealId!}
        reviewId={reviewId!}
        review={review}
        attachments={attachments}
        onReviewChanged={refresh}
        canFlagConventions={deal.visibility !== "private"}
      />
    </div>
  );
}

function ChecklistCard({
  review,
  items,
  onOpen,
  onRerun,
  rerunDisabled,
}: {
  review: Review;
  items: ChecklistItem[];
  onOpen: () => void;
  onRerun: () => void;
  rerunDisabled: boolean;
}) {
  const total = items.length;
  const reviewed = items.filter((i) => !!i.status).length;
  const submitted = !!review.submittedAt;
  const isComplete = total > 0 && reviewed === total;
  // A review marked done with zero items is either a legitimate 0-finding
  // review or one whose full review truncated before the checklist block
  // (older runs that pre-date stop_reason handling). Offer the re-run
  // escape hatch — the backend refuses it once items exist, so this can't
  // duplicate a checklist.
  const canRerun = total === 0 && review.stage === "done" && !submitted;
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold text-sm">Document checklist</h3>
        <button className="text-xs text-ink hover:underline" onClick={onOpen}>
          open
        </button>
      </div>
      {total === 0 ? (
        <div className="text-xs text-slate-400 italic">
          Items appear here after a full review runs.
          {canRerun && (
            <button
              className="block mt-2 not-italic text-ink font-medium hover:underline disabled:text-slate-400 disabled:no-underline"
              disabled={rerunDisabled}
              onClick={onRerun}
              title="The review ran but produced no checklist — re-run the full review to generate one"
            >
              ↻ Re-run full review
            </button>
          )}
        </div>
      ) : (
        <div className="text-xs text-slate-600">
          {submitted ? (
            <>
              <span className="text-emerald-700 font-medium">Submitted</span> ·{" "}
              {review.submittedByName ?? review.submittedByEmail}
            </>
          ) : (
            <>
              {reviewed} / {total} items reviewed
              {isComplete && (
                <span className="ml-1 text-emerald-700 font-medium">— ready to submit</span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4 text-blue-700 shrink-0" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
      <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

function MessageView({ m, isAdmin }: { m: Message; isAdmin: boolean }) {
  const isUser = m.role === "user";
  const author = m.createdByName ?? m.createdByEmail;
  return (
    <div className={`card p-4 ${isUser ? "bg-slate-50" : ""}`}>
      <div className="text-xs text-slate-500 mb-1 flex justify-between">
        <span>{isUser ? author : `Assistant · for ${author}`}</span>
        {isAdmin && m.usage && (
          <span title="input / output / cache-write / cache-read tokens">
            ${m.usage.costUsd.toFixed(4)} · {m.usage.inputTokens}/{m.usage.outputTokens}
            {m.usage.cacheReadTokens > 0 && ` · cache hit ${m.usage.cacheReadTokens}`}
          </span>
        )}
      </div>
      {isUser ? (
        <div className="whitespace-pre-wrap text-sm">{m.content}</div>
      ) : (
        <Markdown>{m.content || (m.error ?? "(empty)")}</Markdown>
      )}
      {m.stopped && (
        <div className="text-amber-700 text-xs mt-2 italic">
          ◼ Stopped — this response is incomplete.
        </div>
      )}
      {m.error && <div className="text-red-600 text-xs mt-2">Error: {m.error}</div>}
    </div>
  );
}

function SharesPanel({ dealId, reviewId }: { dealId: string; reviewId: string }) {
  const [shares, setShares] = useState<Omit<ShareLink, "passwordHash">[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const { shares } = await api.listShares(dealId, reviewId);
    setShares(shares);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, reviewId]);

  const now = Date.now();
  const active = shares.filter((s) => !s.revokedAt && s.expiresAt > now);
  const inactive = shares.filter((s) => s.revokedAt || s.expiresAt <= now);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">External shares</h3>
        <button
          className="text-xs text-ink hover:underline"
          onClick={() => setShowCreate(true)}
        >
          + new
        </button>
      </div>
      {active.length === 0 ? (
        <div className="text-xs text-slate-400 italic">No active shares.</div>
      ) : (
        <ul className="space-y-2 text-sm">
          {active.map((s) => (
            <ShareRow
              key={s.id}
              share={s}
              busy={busy}
              onRevoke={async () => {
                if (!window.confirm("Revoke this share immediately?")) return;
                setBusy(true);
                try {
                  await api.revokeShare(s.id);
                  await refresh();
                } finally {
                  setBusy(false);
                }
              }}
            />
          ))}
        </ul>
      )}
      {inactive.length > 0 && (
        <details className="mt-3 text-xs text-slate-500">
          <summary className="cursor-pointer hover:text-slate-700">
            {inactive.length} expired/revoked
          </summary>
          <ul className="mt-2 space-y-1">
            {inactive.map((s) => (
              <li key={s.id} className="flex justify-between">
                <span className="truncate">/share/{s.token.slice(0, 8)}…</span>
                <span>{s.revokedAt ? "revoked" : "expired"}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {showCreate && (
        <CreateShareModal
          dealId={dealId}
          reviewId={reviewId}
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            await refresh();
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}

function ConventionsPanel({ isAdmin }: { isAdmin: boolean }) {
  const [conventions, setConventions] = useState<TeamConvention[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .listConventions()
      .then((r) => {
        if (!alive) return;
        // Admin endpoint returns all statuses; we only want approved
        // for the review-page panel (what Claude actually applies).
        setConventions(r.conventions.filter((c) => c.status === "approved"));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return null;

  const count = conventions.length;
  // Hide the panel entirely when there's nothing approved AND the user
  // can't do anything about it. Admins still see it so they know the
  // page exists.
  if (count === 0 && !isAdmin) return null;

  return (
    <div className="card p-4">
      <button
        type="button"
        className="w-full flex items-center justify-between mb-1"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <h3 className="font-semibold text-sm">Team conventions</h3>
        <span className="text-xs text-slate-400">{open ? "hide" : `${count} active`}</span>
      </button>
      <div className="text-xs text-slate-500">
        Recurring doc quirks Claude has been told to recognize. Matched findings stay
        in the prose review but are omitted from the checklist.
      </div>
      {open && (
        <ul className="mt-3 space-y-2">
          {count === 0 ? (
            <li className="text-xs text-slate-400 italic">
              No active conventions yet.
            </li>
          ) : (
            conventions.map((c, i) => (
              <li key={c.id} className="text-sm">
                <span className="text-xs text-slate-400 mr-1">#{i + 1}</span>
                <span className="font-medium">{c.title}</span>
                <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">
                  {c.rationale}
                </div>
              </li>
            ))
          )}
        </ul>
      )}
      {isAdmin && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <Link
            to="/admin/conventions"
            className="text-xs text-ink hover:underline"
          >
            Manage conventions →
          </Link>
        </div>
      )}
    </div>
  );
}

function ShareRow({
  share,
  busy,
  onRevoke,
}: {
  share: Omit<ShareLink, "passwordHash">;
  busy: boolean;
  onRevoke: () => void;
}) {
  const url = `${window.location.origin}/share/${share.token}`;
  const [copied, setCopied] = useState(false);
  const expiresIn = share.expiresAt - Date.now();
  const expiresLabel = formatRemaining(expiresIn);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  return (
    <li className="border border-slate-200 rounded-md p-2">
      <div className="flex items-center justify-between gap-2">
        <code
          className="text-xs truncate text-slate-600 cursor-pointer hover:text-ink"
          title={url}
          onClick={copy}
        >
          {copied ? "copied!" : `…/${share.token.slice(0, 12)}…`}
        </code>
        <button
          className="text-xs text-red-600 hover:underline shrink-0"
          disabled={busy}
          onClick={onRevoke}
        >
          revoke
        </button>
      </div>
      <div className="text-xs text-slate-400 mt-1">
        expires in {expiresLabel}
        {share.createdByName && <> · by {share.createdByName}</>}
      </div>
    </li>
  );
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "expired";
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  if (hours >= 1) return `${hours}h`;
  const mins = Math.floor(ms / (60 * 1000));
  return `${mins}m`;
}

function CreateShareModal({
  dealId,
  reviewId,
  onClose,
  onCreated,
}: {
  dealId: string;
  reviewId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [password, setPassword] = useState("");
  const [hours, setHours] = useState<number>(DEFAULT_SHARE_DURATION_HOURS);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ url: string; password: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<"url" | "pw" | null>(null);

  async function copyText(text: string, kind: "url" | "pw") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignore
    }
  }

  async function submit() {
    if (password.length < 4) {
      setErr("Password must be at least 4 characters.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { share } = await api.createShare({
        dealId,
        reviewId,
        password,
        expiresInHours: hours,
      });
      const url = `${window.location.origin}/share/${share.token}`;
      setCreated({ url, password });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-20">
      <div className="card w-full max-w-md p-6">
        <h2 className="font-semibold text-lg mb-4">
          {created ? "Share created" : "Create external share"}
        </h2>
        {!created ? (
          <>
            <p className="text-sm text-slate-600 mb-4">
              Generates a password-protected URL with read-only access to this review's
              chat and the deal's notes. No document downloads.
            </p>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="text"
              className="input mb-3 font-mono"
              placeholder="Set a password to share"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <label className="block text-sm font-medium mb-1">Expires in</label>
            <select
              className="input mb-4"
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
            >
              {SHARE_DURATION_OPTIONS.map((o) => (
                <option key={o.hours} value={o.hours}>
                  {o.label}
                </option>
              ))}
            </select>
            {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={busy || password.length < 4}
                onClick={submit}
              >
                {busy ? "Creating…" : "Create share"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-3">
              Send the URL and password to your recipient. The password is shown only once
              here — copy it now.
            </p>
            <label className="block text-xs uppercase tracking-wide text-slate-400 mt-2">
              URL
            </label>
            <div className="flex gap-1 mt-1">
              <input className="input font-mono text-xs" readOnly value={created.url} />
              <button
                className="btn-secondary text-xs"
                onClick={() => copyText(created.url, "url")}
              >
                {copied === "url" ? "copied" : "copy"}
              </button>
            </div>
            <label className="block text-xs uppercase tracking-wide text-slate-400 mt-3">
              Password
            </label>
            <div className="flex gap-1 mt-1">
              <input className="input font-mono text-xs" readOnly value={created.password} />
              <button
                className="btn-secondary text-xs"
                onClick={() => copyText(created.password, "pw")}
              >
                {copied === "pw" ? "copied" : "copy"}
              </button>
            </div>
            <div className="flex justify-end mt-6">
              <button className="btn-primary" onClick={onCreated}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The opening request for a review's first pass. Must agree with the scope
 * chosen at creation (quick pass / skipped sections) — the system prompt
 * carries the scope, and a message asking for "the full review" would
 * contradict it.
 */
function kickoffText(review: Review, label?: string): string {
  const docs = label ? `${label} documents` : "the documents";
  const skipped = review.skipSections ?? [];
  const scope = review.quickPass
    ? "Please run a quick pass."
    : skipped.length > 0
      ? `Please run the review, skipping ${skipped.map((s) => REVIEW_SECTION_LABEL[s]).join(", ")} as selected for this review.`
      : null;
  switch (review.reviewType) {
    case "redline_review":
      return `Counterparty redline of ${docs}. ${scope ?? "Please run the full redline review."}`;
    case "standalone_review":
      return `Standalone review of ${docs}. ${scope ?? "Please run the full review."}`;
    default:
      return `First draft of ${docs}. ${scope ?? "Please run the full review against the term sheet and intake sheet."}`;
  }
}
